/**
 * Image upload component with drag and drop
 */
import { useState, useRef, ChangeEvent, DragEvent } from 'react';
import { Button } from '@/components/ui/Button';

interface ImageUploadProps {
  onUpload: (file: File) => void | Promise<void>;
  currentImage?: string;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  uploading?: boolean;
}

export function ImageUpload({
  onUpload,
  currentImage,
  maxSizeMB = 5,
  acceptedFormats = ['image/jpeg', 'image/png', 'image/webp'],
  uploading = false
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!acceptedFormats.includes(file.type)) {
      return `Invalid format. Accepted: ${acceptedFormats.join(', ')}`;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File too large. Max size: ${maxSizeMB}MB`;
    }
    return null;
  };

  const handleFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    try {
      await onUpload(file);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Error al subir la imagen. Por favor, intenta de nuevo.');
      // Reset preview on error
      setPreview(currentImage || null);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // Call handleFile without awaiting - errors are handled inside handleFile
      void handleFile(e.target.files[0]);
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // Call handleFile without awaiting - errors are handled inside handleFile
      void handleFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      {preview && (
        <div className="relative w-full h-48 bg-surface-2 rounded-lg overflow-hidden border-2 border-hairline">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          {!uploading && (
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute top-2 right-2 bg-danger/100 hover:bg-danger/90 text-white rounded-full p-2 transition-colors shadow-lg"
              title="Remove image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          dragActive
            ? 'border-info/30 bg-info/10 scale-105'
            : 'border-hairline hover:border-gray-400 dark:hover:border-gray-500 bg-surface-2'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleChange}
          className="hidden"
          disabled={uploading}
        />

        <div className="mb-4">
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-info/30"></div>
              <p className="text-ink-muted font-medium">Subiendo imagen...</p>
            </div>
          ) : (
            <>
              <div className="text-5xl mb-3">
                {dragActive ? '📥' : '🖼️'}
              </div>
              <p className="font-medium text-ink-muted mb-1">
                Arrastra y suelta tu imagen aquí
              </p>
              <p className="text-sm text-ink-subtle">o</p>
            </>
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-surface hover:bg-surface-2"
        >
          📁 Elegir Archivo
        </Button>

        <p className="text-xs text-ink-subtle mt-3">
          Tamaño máximo: {maxSizeMB}MB • Formatos: JPG, PNG, WEBP
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg p-3">
          <p className="text-sm text-danger flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        </div>
      )}
    </div>
  );
}
